import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Field, useFormattedDate } from '@rtkwlf/fenrir-react';
import { faInfoCircle } from '@rtkwlf/pro-solid-svg-icons/faInfoCircle';
import { faLock } from '@rtkwlf/pro-solid-svg-icons/faLock';
import type {
  BlueprintWithOrganizationData,
  ConciergeTierInfo,
} from '@rtkwlf/track-public';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';

import { useGetSpidrFrameworkCategoriesOptions } from '@apis/track-public/query-options';
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@packages/fenrir/components';
import { Markdown } from 'ae-components/markdown/Markdown';

import { ConciergeTierBadge } from '../../../../components/concierge-tier-badge/concierge-tier-badge';
import { FavoriteButton } from '../../../../components/favorite-button';
import { FrameworkOverviewAccordion } from '../../../../components/framework-accordion/framework-overview-accordion';
import { NewBadge } from '../../../../components/new-badge';
import { routes } from '../../../../constants/routes';
import { useSecurityJourneyTranslations } from '../../../../locales/use-security-journey-translations';

import styles from './blueprint-details-content.module.css';

/* -------------------------------------------------------------------------------------------------
 *  BlueprintDetailList
 * -----------------------------------------------------------------------------------------------*/
type BlueprintDetailInfoProps = {
  value: string | string[];
  color?: string;
};

const BlueprintDetailInfo: React.FunctionComponent<
  BlueprintDetailInfoProps
> = ({ value, color }) => {
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Typography variant="body2" sx={{ color }}>
          -
        </Typography>
      );
    }

    if (value.length === 1) {
      return (
        <Typography variant="body2" sx={{ color }}>
          {value[0]}
        </Typography>
      );
    }

    return (
      <ul className={styles['blueprint-details-content-info-list']}>
        {value.map((v) => {
          return (
            <Typography key={v} component="li" variant="body2" sx={{ color }}>
              {v}
            </Typography>
          );
        })}
      </ul>
    );
  }
  return (
    <Typography variant="body2" sx={{ color }}>
      {value === '' ? '-' : value}
    </Typography>
  );
};

/* -------------------------------------------------------------------------------------------------
 *  BlueprintDetailsContent
 * -----------------------------------------------------------------------------------------------*/
type BlueprintDetailsContentProps = {
  blueprint: BlueprintWithOrganizationData;
  orgTier: ConciergeTierInfo;
};

export const BlueprintDetailsContent: React.FunctionComponent<
  BlueprintDetailsContentProps
> = ({ blueprint, orgTier }) => {
  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Format lastDeliveredAt date
   */
  const formattedDate = useFormattedDate(
    blueprint.lastDeliveredAt ?? new Date(),
    'prettyDate',
  );

  /**
   * Check if blueprint is a higher tier than the organization and the organization is not legacy
   */
  const blueprintIsHigherTier =
    !orgTier.legacy && Number(blueprint.tier) > Number(orgTier.tier);

  const { data } = useQuery(useGetSpidrFrameworkCategoriesOptions());

  return (
    <Stack
      spacing={4}
      padding="var(--page-panel-padding)"
      direction="column"
      height="100%"
      width="100%"
      overflow="auto"
      data-testid="blueprint-detail-content"
    >
      <Stack spacing={1} direction="column">
        <Stack
          direction="row"
          justifyContent="flex-end"
          paddingBottom="var(--c3-space-4)"
        >
          <FavoriteButton
            blueprintID={blueprint.id}
            className={styles['favorite-button-margin']}
          />
        </Stack>
        <Stack spacing={3} direction="row" alignItems="flex-start">
          <Typography variant="h3" alignContent="center">
            {blueprint.name}
          </Typography>
          {blueprintIsHigherTier && (
            <span
              className={styles['blueprint-detail-content-lock-icon']}
              data-testid="blueprint-detail-content-lock-icon"
            >
              <FontAwesomeIcon icon={faLock} />
            </span>
          )}
          <NewBadge createdAt={blueprint.createdAt} />
        </Stack>
        {!orgTier.legacy && (
          <Stack spacing={0.5} direction="row">
            <ConciergeTierBadge.Spidr tier={blueprint.tier} />
            <Tooltip
              title={t(
                `spidrLibrary.blueprintDetailsContent.tierTooltip.${blueprint.tier}`,
              )}
              placement="right"
              arrow
              data-testid="tier-tooltip"
            >
              <IconButton size="small">
                <FontAwesomeIcon icon={faInfoCircle} size="sm" />
              </IconButton>
            </Tooltip>
          </Stack>
        )}
      </Stack>
      <Stack spacing={4} direction="column">
        <Field.Root>
          <Field.Label>
            <Typography variant="body2" color="textPrimary">
              {t('spidrLibrary.blueprintDetailsContent.description')}
            </Typography>
          </Field.Label>
          <Field.Content>
            <BlueprintDetailInfo
              value={blueprint.description}
              color="text.secondary"
            />
          </Field.Content>
        </Field.Root>
        <Field.Root>
          <Field.Label>
            <Typography variant="body2" color="textPrimary">
              {t('spidrLibrary.blueprintDetailsContent.expectedOutcomes')}
            </Typography>
          </Field.Label>
          <Field.Content>
            <Box sx={{ color: 'text.secondary' }}>
              <Markdown
                data-blank={blueprint.outcome === ''}
                className={styles['outcome-textbox']}
                value={
                  blueprint.outcome === '' ? '&#65112;' : blueprint.outcome
                }
                style={{ color: 'inherit' }}
              />
            </Box>
          </Field.Content>
        </Field.Root>
        {blueprint.timeCommitment > 0 && (
          <Field.Root>
            <Field.Label>
              <Typography variant="body2" color="textPrimary">
                {t(
                  'spidrLibrary.blueprintDetailsContent.estimatedTimeCommitment.label',
                )}
              </Typography>
            </Field.Label>
            <Field.Content>
              <Typography variant="body2" color="textSecondary">
                {t(
                  'spidrLibrary.blueprintDetailsContent.estimatedTimeCommitment.value',
                  {
                    count: blueprint.timeCommitment,
                  },
                )}
              </Typography>
            </Field.Content>
          </Field.Root>
        )}
        <Field.Root>
          <Field.Label>
            <Typography variant="body2" color="textPrimary">
              {t('spidrLibrary.blueprintDetailsContent.technicalDomains')}
            </Typography>
          </Field.Label>
          <Field.Content>
            <BlueprintDetailInfo
              value={blueprint.technicalDomains}
              color="text.secondary"
            />
          </Field.Content>
        </Field.Root>
        <Box width="100%">
          <FrameworkOverviewAccordion
            framework="nist"
            bpId={blueprint.id}
            elements={blueprint.nist}
            totalFrameworks={blueprint.nist.length}
            totalGroups={
              data
                ? Object.values(data.nistCategories).reduce(
                    (sum, array) => sum + array.length,
                    0,
                  )
                : 0
            }
            numberedList
          />
          <FrameworkOverviewAccordion
            framework="nist2"
            bpId={blueprint.id}
            elements={blueprint.nist2}
            totalFrameworks={blueprint.nist2.length}
            totalGroups={
              data
                ? Object.values(data.nist2Categories).reduce(
                    (sum, array) => sum + array.length,
                    0,
                  )
                : 0
            }
            numberedList
          />
          <FrameworkOverviewAccordion
            framework="cis"
            bpId={blueprint.id}
            elements={blueprint.cis}
            totalFrameworks={blueprint.cis.length}
            totalGroups={
              data ? Object.keys(data.cisControlsToSafeguards).length : 0
            }
          />
        </Box>
        <Field.Root>
          <Field.Label>
            <Typography variant="body2" color="textPrimary">
              {t('spidrLibrary.blueprintDetailsContent.lastDelivered')}
            </Typography>
          </Field.Label>
          <Field.Content>
            {blueprint.lastDeliveredAt && blueprint.lastDeliveredSpidrId ? (
              <Link
                to={routes.spidrs.spidrID.index({
                  spidrID: blueprint.lastDeliveredSpidrId,
                })}
              >
                <Typography variant="body2" color="textSecondary">
                  {formattedDate}
                </Typography>
              </Link>
            ) : (
              <Typography variant="body2" color="textSecondary">
                -
              </Typography>
            )}
          </Field.Content>
        </Field.Root>
      </Stack>
    </Stack>
  );
};
